﻿<script setup lang="ts">
    import { codeEditPermissionsForLibrary, requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { sqlQuery } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper.js';
    import MultiScopeSelector from '@/shared/components/scopeselector/MultiScopeSelector.vue';
    import useShow from '@/shared/composables/useShow.js';
    import { computed, onMounted, ref, watchEffect } from 'vue';

    type Facility = {
        identity: string;
        description: string;
    };

    type Category = {
        identity: string;
        description: string;
        facility: string;
    };

    type Format = {
        description: string;
        facility: string;
        category: string | null;
    };

    type SubmitPayload = {
        facilities: string[];
        category: string | null;
        description: string;
    };

    const { showError } = useShow();

    const props = defineProps<{
        categories: Category[];
        ensType: string;
        originalFormat: Format;
        facilities: Facility[];
    }>();

    const emit = defineEmits<{
        close: [];
        submit: [payload: SubmitPayload];
    }>();

    const description = ref<string>(props.originalFormat.description);
    const selectedFacilities = ref<string[]>([props.originalFormat.facility]);
    const selectedCategory = ref<string | null>(props.originalFormat.category);
    const loadingExistingFormats = ref(false);
    const existingFormatDescriptions = ref<Record<string, string[]>>({});
    const disabledFacilities = ref<string[]>([]);

    onMounted(async () => {
        const permissions = await codeEditPermissionsForLibrary(props.ensType);
        disabledFacilities.value = props.facilities
            .map(f => f.identity)
            .filter(id => !permissions[id]);

        // Remove selected facilities that become disabled after permissions load
        selectedFacilities.value = selectedFacilities.value.filter(
            id => !disabledFacilities.value.includes(id)
        );
    });

    // Categories present in all selected facilities (intersection)
    const filteredCategories = computed((): Category[] => {
        if (selectedFacilities.value.length === 0)
            return [];

        const categorySetsPerFacility = selectedFacilities.value.map(f =>
            new Set(props.categories.filter(c => c.facility === f).map(c => c.identity))
        );

        return props.categories
            .filter(c => c.facility === selectedFacilities.value[0])
            .filter(c => categorySetsPerFacility.every(set => set.has(c.identity)));
    });

    const normalizedDescription = computed(() => description.value.toLowerCase().trim());

    const isDescriptionUnique = computed(() =>
        !Object.values(existingFormatDescriptions.value).flat().some(x => x.toLowerCase().trim() === normalizedDescription.value)
    );

    const descriptionCollisionFacilities = computed(() =>
        Object.entries(existingFormatDescriptions.value)
            .filter(([, descriptions]) => descriptions.some(x => x.toLowerCase().trim() === normalizedDescription.value))
            .map(([facility]) => facility)
    );

    const canSubmit = computed(() =>
        selectedFacilities.value.length > 0
        && !!selectedCategory.value
        && !!description.value.trim()
        && !loadingExistingFormats.value
        && isDescriptionUnique.value
    );

    const loadFormats = async () => {
        existingFormatDescriptions.value = {};

        if (selectedFacilities.value.length === 0 || !selectedCategory.value)
            return;

        loadingExistingFormats.value = true;

        const scopes = selectedFacilities.value.map(f => `'${f.replaceAll('\'', '\'\'')}'`).join(', ');

        const query = props.ensType === 'TagFormat'
            ? `SELECT [scope], [description]
                            FROM [TagFormat]
                            WHERE [isValid] = 1 AND [scope] IN (${scopes}) AND [tagCategory] = @category
                            ORDER BY [description]`
            : `SELECT [scope], [description]
                            FROM [DocumentNumberingFormat]
                            WHERE [isValid] = 1 AND [scope] IN (${scopes}) AND [docCategory] = @category
                            ORDER BY [description]`;

        try {
            const response = await sqlQuery(http, {
                query,
                arguments: { category: selectedCategory.value! }
            });
            existingFormatDescriptions.value = Object.fromEntries(
                selectedFacilities.value.map(facility => [
                    facility,
                    (response.data as { scope: string; description: string }[])
                        .filter(x => x.scope === facility)
                        .map(x => x.description)
                ])
            );
        } catch (ex) {
            showError(ex);
        }

        loadingExistingFormats.value = false;
    };

    const onClose = () => {
        emit('close');
    };

    const onClickSubmit = () => {
        emit('submit', {
            facilities: selectedFacilities.value,
            category: selectedCategory.value,
            description: description.value
        });
        emit('close');
    };

    watchEffect(() => {
        if (selectedFacilities.value.length === 0) {
            return;
        }
        selectedCategory.value = filteredCategories.value.find(x => x.identity === selectedCategory.value)?.identity ?? null;
    });

    watchEffect(async () => {
        await loadFormats();
    });
</script>

<template>
    <b-modal
        has-modal-card
        model-value
        @close="onClose">
        <div class="modal-card">
            <header class="modal-card-head">
                <p class="modal-card-title">
                    <b>Clone format from {{ originalFormat.facility }}</b>
                </p>
            </header>
            <section class="modal-card-body">
                <b-field label="Clone to facilities">
                    <multi-scope-selector
                        v-model:selected-scopes="selectedFacilities"
                        :scopes="facilities"
                        :disabled-scopes="disabledFacilities" />
                </b-field>
                <b-field
                    label="Category"
                    horizontal>
                    <b-select
                        v-model="selectedCategory"
                        placeholder="Select category"
                        :disabled="selectedFacilities.length === 0"
                        expanded>
                        <option
                            v-for="category in filteredCategories"
                            :key="category.identity"
                            :value="category.identity">
                            {{ category.description }}
                        </option>
                    </b-select>
                </b-field>
                <b-field
                    label="Description"
                    :type="isDescriptionUnique ? '' : 'is-danger'"
                    :message="isDescriptionUnique ? '' : `A format with the same description already exists in ${descriptionCollisionFacilities.join(', ')}`"
                    horizontal>
                    <b-input
                        v-model="description"
                        :loading="loadingExistingFormats"
                        required />
                </b-field>
            </section>
            <footer class="modal-card-foot">
                <b-field class="is-grouped-right">
                    <b-button
                        label="Cancel"
                        @click="onClose" />
                    <b-button
                        v-require-can-edit-code="{ libraryName: ensType, scopes: selectedFacilities }"
                        label="Clone"
                        type="is-primary"
                        :disabled="!canSubmit"
                        @click="onClickSubmit" />
                </b-field>
            </footer>
        </div>
    </b-modal>
</template>

<style scoped>
.modal-card {
    max-width: 900px;
}

.modal-card-foot {
    /* Right-justify buttons to match .dialog button placement */
    justify-content: flex-end;
}
</style>
